/**
 * What happens when the list is performing, in one sentence with two
 * inputs — "each check grows the next drops by [X] %, up to [N] SMS" — and
 * the growth ladder it means for THIS container's default amount.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import type { DropGenTextMix } from '@trafficpulse/shared';

import { HintDot, NumberField } from '@/components/ui';

import type { DraftScaling } from '../../../../drop-generator.types';
import { ProvenTag } from '../../../shared/texts/ProvenTag';
import { INPUT_CLASS } from '../../general/fields/field-chrome';

const LADDER_STEPS = 4;

/** 300 → 375 → 469 → 586 → 600 (cap): the first steps, capped. */
export function growthLadder(base: number, stepPct: number, maxAmount: number | null): string[] {
  const out: string[] = [base.toLocaleString('en-US')];
  for (let step = 1; step <= LADDER_STEPS; step += 1) {
    // The engine's own formula: base × (1 + pct)^steps, rounded once.
    const next = Math.round(base * Math.pow(1 + stepPct / 100, step));
    if (maxAmount !== null && next >= maxAmount) {
      out.push(`${maxAmount.toLocaleString('en-US')} (cap)`);
      return out;
    }
    out.push(next.toLocaleString('en-US'));
  }
  return [...out, '…'];
}

export const ScalingAction: FC<{
  scaling: DraftScaling;
  defaultAmount: number;
  onPatch: (patch: Partial<DraftScaling>) => void;
  /** Phase 37 — the texts scaled drops send (mirror). */
  proven?: DropGenTextMix;
}> = ({ scaling, defaultAmount, onPatch, proven }) => (
  <div className="flex flex-col gap-1.5 text-xs text-muted-foreground">
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1.5">
      the next drops grow by
      <NumberField
        value={scaling.stepPct}
        onChange={(v) => onPatch({ stepPct: v })}
        min={1}
        max={500}
        className={`${INPUT_CLASS} h-7 w-16 text-xs`}
        aria-label="Growth per performing check, percent"
      />
      % per check with new results · up to
      <input
        type="number"
        min={1}
        max={1000000}
        value={scaling.maxAmount ?? ''}
        placeholder="no cap"
        onChange={(e) => onPatch({ maxAmount: e.target.value === '' ? null : Number(e.target.value) })}
        className={`${INPUT_CLASS} h-7 w-24 text-xs ${scaling.maxAmount === null ? 'text-muted-foreground' : ''}`}
        aria-label="Amount cap for scaled drops"
      />
      SMS
      <HintDot label="One step per check — only when new sends have settled since the last step (the same results never scale twice) — compounding. The cap stops growth; empty = no amount cap (ten steps at most). Only unsent drafts grow — sent ones never change. A new day starts at step 0." />
      <ProvenTag mix={proven} what="The scaled drops" />
    </div>
    <p className="font-mono text-[11px] tabular-nums text-muted-foreground/80">
      {defaultAmount.toLocaleString('en-US')} per drop → {growthLadder(defaultAmount, scaling.stepPct, scaling.maxAmount).slice(1).join(' → ')}
    </p>
  </div>
);
